import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { AppService } from 'app/app.service';
import { AccountFeature } from 'app/constants/constants';
import { SemantriaAccountService } from 'app/providers/semantria-api/semantria-account.service';
import { SemantriaAPIService } from 'app/providers/semantria-api.service';
import {
  DownloadTasksService,
  Task,
  TaskPayload,
  TaskType,
} from 'app/providers/tasks-api/download.service';
import { EnableClassTelemetry } from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';
import { AccountUtils } from 'app/utils/account.utils';

export enum SmartSummaryStyle {
  DATA_ANALYST = 'DATA_ANALYST',
  STATISTICAL = 'STATISTICAL',
}

export enum AnalyticItemImpact {
  POSITIVE = 'POSITIVE',
  NEGATIVE = 'NEGATIVE',
}

export interface SmartSummaryScenarioParameters {
  length: number;
  style: SmartSummaryStyle;
  language: string;
  analyticItemType?: string;
  analyticItemName?: string;
  analyticItemImpact?: AnalyticItemImpact;
}

export interface SmartSummaryDocumentConfig {
  source: string;
  groupId: string;
  projectId: string;
  collectionId: string;
  analysisId: string;
  driverAnalysisId: string;
  breakdownFieldValue: string;
  driverFieldName: string;
  analyticItem: boolean;
  documentQuery: string;
  timezone: string;
  previousStart?: Date;
  previousEnd?: Date;
  currentStart?: Date;
  currentEnd?: Date;
  scenarioParameters: SmartSummaryScenarioParameters;
}

export interface SmartSummaryDocumentPayload extends TaskPayload {
  source: string;
  filename: string;
}

export interface SmartSummaryDocumentResult {
  exportId: string;
  fileSize: number;
  docCount: number;
}

export type SmartSummaryDocumentTaskResult = Task<
  SmartSummaryDocumentPayload,
  SmartSummaryDocumentResult
>;

@EnableClassTelemetry()
@Injectable({ providedIn: 'root' })
export class SmartSummaryService extends SemantriaAPIService {
  private readonly FEATURE_LLM: AccountFeature = 'LARGE_LANGUAGE_MODELS';
  private readonly EXTERNAL_RESOURCE_TYPE_SPOT_E_SCENARIO = 'SPOT_E_SCENARIO';
  private readonly EXTERNAL_RESOURCE_TYPE_SPOT_E_SCENE = 'SPOT_E_SCENE';

  constructor(
    _httpClient: HttpClient,
    _systemService: SystemService,
    private accountService: SemantriaAccountService,
    private downloadTasksService: DownloadTasksService
  ) {
    super(_httpClient, _systemService);
  }

  async hasPermissionForSummaryByDriverField(): Promise<boolean> {
    const accountDetails = await this.accountService.sub.latest();

    return (
      AccountUtils.hasFeature(accountDetails, this.FEATURE_LLM) &&
      AccountUtils.hasExternalResource(
        accountDetails,
        AppService.getConfig().spote.smartSummaryByDriverFieldSceneId,
        undefined,
        this.EXTERNAL_RESOURCE_TYPE_SPOT_E_SCENE
      )
    );
  }

  async hasPermissionForSummaryByAnalyticItem(): Promise<boolean> {
    const accountDetails = await this.accountService.sub.latest();
    return (
      AccountUtils.hasFeature(accountDetails, this.FEATURE_LLM) &&
      AccountUtils.hasExternalResource(
        accountDetails,
        AppService.getConfig().spote.smartSummaryByAnalyticItemSceneId,
        undefined,
        this.EXTERNAL_RESOURCE_TYPE_SPOT_E_SCENE
      )
    );
  }

  createSummary(
    config: SmartSummaryDocumentConfig,
    fileNameProvider: () => string
  ): Promise<SmartSummaryDocumentTaskResult> {
    const payload: SmartSummaryDocumentPayload = {
      _type: TaskType.SMART_SUMMARY_DOCUMENT,
      filename: fileNameProvider(),
      ...config,
    };

    return this.downloadTasksService.createTask(payload);
  }
}
